import { type ReactNode } from "react";
import {
  Navigate,
  Outlet,
  Route,
  type RouteObject,
  Routes,
} from "react-router";

import { m } from "@vega-core/paraglide/messages";
import { buildTimeFlags } from "@vega-core/utils/buildTimeFlags";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { PageContainerWithNavigation } from "@vega-modules/components/PageContainerWithNavigation/PageContainerWithNavigation";
import { GPSettingsPaths } from "@vega-modules/paths";
import { Display } from "@vega-modules/scenes/Settings/sections/Display";

import { CaseManagementPage } from "../pages/CaseManagementPage";
import { CASManagementPage } from "../pages/CASManagementPage";
import { CoInvestmentPage } from "../pages/CoInvestmentPage";
import { CollaboratorsPage } from "../pages/CollaboratorsPage";
import { NotificationsPage } from "../pages/Notifications";
import { ProfilePage } from "../pages/ProfilePage";
import { ReportingApiKeysPage } from "../pages/ReportingApiKeys/ReportingApiKeysPage";
import { SupportPage } from "../pages/SupportPage";

import { SettingNavigationWrapper } from "./SettingWrapper/SettingNavigationWrapper";

const SettingsDefaultRedirect = () => {
  const defaultPath = isGPCoInvestPortal
    ? GPSettingsPaths.CoInvestment
    : GPSettingsPaths.Collaborators;

  return <Navigate replace to={`../${defaultPath}`} />;
};

const SettingsPageContainerWithNavigation = ({
  isStandaloneCoInvestPortal = false,
  children,
}: {
  isStandaloneCoInvestPortal?: boolean;
  children: ReactNode;
}) => {
  const pageContainerProps = isStandaloneCoInvestPortal
    ? {
        hideFooter: true,
        horizontalSubnav: true,
      }
    : {
        showNavbar: true,
      };

  return (
    <PageContainerWithNavigation {...pageContainerProps}>
      {children}
    </PageContainerWithNavigation>
  );
};

export const settingsRouteObjects: RouteObject[] = [
  ...(!isGPCoInvestPortal
    ? ([
        {
          element: (
            <SettingNavigationWrapper>
              <SettingsPageContainerWithNavigation>
                <div className="w-full flex-1">
                  <Outlet />
                </div>
              </SettingsPageContainerWithNavigation>
            </SettingNavigationWrapper>
          ),
          children: [
            {
              element: <CollaboratorsPage />,
              path: GPSettingsPaths.Collaborators,
            },
            {
              element: <ReportingApiKeysPage />,
              path: GPSettingsPaths.ReportingApiKeys,
            },
          ],
        },
      ] satisfies RouteObject[])
    : []),
  {
    element: (
      <SettingNavigationWrapper>
        <SettingsPageContainerWithNavigation
          isStandaloneCoInvestPortal={isGPCoInvestPortal}
        >
          <div className="w-full flex-1 mx-auto max-w-5xl">
            <Outlet />
          </div>
        </SettingsPageContainerWithNavigation>
      </SettingNavigationWrapper>
    ),
    children: [
      ...(!isGPCoInvestPortal
        ? ([
            {
              element: <CaseManagementPage />,
              path: GPSettingsPaths.CaseManagement,
            },
            ...(buildTimeFlags.reporting_gp_settings_cas_management
              ? ([
                  {
                    element: <CASManagementPage />,
                    path: GPSettingsPaths.CASManagement,
                  },
                ] satisfies RouteObject[])
              : []),
          ] satisfies RouteObject[])
        : []),
      {
        element: <CoInvestmentPage />,
        path: GPSettingsPaths.CoInvestment,
      },
      { element: <ProfilePage />, path: GPSettingsPaths.Profile },
      ...(!isGPCoInvestPortal
        ? ([
            {
              element: <NotificationsPage />,
              path: GPSettingsPaths.Notifications,
            },
          ] satisfies RouteObject[])
        : []),
      { element: <SupportPage />, path: GPSettingsPaths.Support },
      {
        element: <Display title={m.settings_display()} />,
        path: GPSettingsPaths.Display,
      },
      { element: <SettingsDefaultRedirect />, path: "*" },
    ],
  },
];

export const SettingsRoutes = () => (
  <Routes>
    {!isGPCoInvestPortal && (
      <Route
        element={
          <SettingNavigationWrapper>
            <SettingsPageContainerWithNavigation>
              <div className="w-full flex-1">
                <Outlet />
              </div>
            </SettingsPageContainerWithNavigation>
          </SettingNavigationWrapper>
        }
      >
        <Route
          element={<CollaboratorsPage />}
          index
          path={GPSettingsPaths.Collaborators}
        />
        <Route
          element={<ReportingApiKeysPage />}
          path={GPSettingsPaths.ReportingApiKeys}
        />
      </Route>
    )}
    <Route
      element={
        <SettingNavigationWrapper>
          <SettingsPageContainerWithNavigation
            isStandaloneCoInvestPortal={isGPCoInvestPortal}
          >
            <div className="w-full flex-1 mx-auto max-w-5xl">
              <Outlet />
            </div>
          </SettingsPageContainerWithNavigation>
        </SettingNavigationWrapper>
      }
    >
      {!isGPCoInvestPortal && (
        <>
          <Route
            element={<CaseManagementPage />}
            path={GPSettingsPaths.CaseManagement}
          />
          {buildTimeFlags.reporting_gp_settings_cas_management ? (
            <Route
              element={<CASManagementPage />}
              path={GPSettingsPaths.CASManagement}
            />
          ) : null}
        </>
      )}
      <Route
        element={<CoInvestmentPage />}
        path={GPSettingsPaths.CoInvestment}
      />
      <Route element={<ProfilePage />} path={GPSettingsPaths.Profile} />
      {!isGPCoInvestPortal && (
        <Route
          element={<NotificationsPage />}
          path={GPSettingsPaths.Notifications}
        />
      )}
      <Route element={<SupportPage />} path={GPSettingsPaths.Support} />
      <Route
        element={<Display title={m.settings_display()} />}
        path={GPSettingsPaths.Display}
      />
      <Route element={<SettingsDefaultRedirect />} path="*" />
    </Route>
  </Routes>
);
